import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { STATUS_LABELS, INFRA_TYPES, PETITION_TYPES, formatMoney, formatDate } from "@/lib/constants";
import { Printer } from "lucide-react";
import { useLogo } from "@/lib/useLogo";

const SECTIONS = [
  { key: "info", label: "Köy bilgileri" },
  { key: "totals", label: "Toplam tutarlar" },
  { key: "work", label: "Son çalışmalar" },
  { key: "infra", label: "Altyapı projeleri" },
  { key: "investments", label: "Yatırımlar" },
  { key: "petitions", label: "Bekleyen dilekçeler" },
  { key: "signatures", label: "İmza alanları" },
];
const STORE_KEY = "khgb_print_sections";

function loadSections() {
  const all = Object.fromEntries(SECTIONS.map((s) => [s.key, true]));
  try {
    return { ...all, ...JSON.parse(localStorage.getItem(STORE_KEY) || "{}") };
  } catch {
    return all;
  }
}

const SectionPicker = ({ value, onChange }) => (
  <div className="no-print max-w-[210mm] mx-auto mb-3 bg-white rounded-md border border-slate-300 p-3" data-testid="print-section-picker">
    <div className="text-xs font-semibold text-slate-600 mb-2">Formda yer alacak bölümler (bu bilgisayarda hatırlanır)</div>
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {SECTIONS.map((s) => (
        <label key={s.key} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
          <input type="checkbox" checked={!!value[s.key]} className="h-4 w-4 accent-emerald-700"
                 onChange={(e) => onChange({ ...value, [s.key]: e.target.checked })}
                 data-testid={`print-section-toggle-${s.key}`} />
          {s.label}
        </label>
      ))}
    </div>
  </div>
);

const MAX_ROWS = 5;
const HEADING_FONT = { fontFamily: "'Work Sans', Arial, sans-serif", letterSpacing: 0 };

const Section = ({ title, headers, rows, empty, more, testId }) => (
  <section className="mt-3 break-inside-avoid" data-testid={testId}>
    <h2 style={HEADING_FONT} className="text-[12px] font-bold text-slate-900 border-b border-slate-300 pb-0.5 mb-1">{title}</h2>
    {rows.length === 0 ? (
      <div className="text-[10px] text-slate-500">{empty}</div>
    ) : (
      <table className="w-full text-[10px] leading-tight border-collapse table-fixed">
        <thead>
          <tr>{headers.map((h) => <th key={h} className="text-left font-semibold text-slate-700 border-b border-slate-200 py-[2px] pr-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="py-[2px] pr-2 border-b border-slate-100 whitespace-nowrap overflow-hidden text-ellipsis">{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    )}
    {more > 0 && <div className="text-[9px] text-slate-500 mt-0.5">… ve {more} kayıt daha (tam liste için PDF/Excel raporu)</div>}
  </section>
);

const Box = ({ label, value }) => (
  <div className="border border-slate-300 rounded px-2 py-1 min-w-0">
    <div className="text-[9px] text-slate-500">{label}</div>
    <div className="text-[12px] font-bold text-slate-900 truncate">{value}</div>
  </div>
);

export default function VillagePrintPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState({});
  const [error, setError] = useState("");
  const [show, setShow] = useState(loadSections);
  const { url: logoUrl } = useLogo();
  const [union, setUnion] = useState({ name: "Köylere Hizmet Götürme Birliği", address: "", phone: "" });
  useEffect(() => {
    api.get("/settings/union").then((r) => setUnion(r.data));
  }, []);
  const contact = [union.address, union.phone ? `Tel: ${union.phone}` : ""].filter(Boolean).join(" · ");

  const updateShow = (next) => {
    setShow(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    api.get(`/villages/${id}/summary`).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Köy yüklenemedi"));
    api.get("/categories").then((r) => setCats(Object.fromEntries(r.data.map((c) => [c.id, c.name]))));
  }, [id]);

  useEffect(() => {
    if (data) {
      document.title = `${data.village.name} Köy Özeti`;
    }
  }, [data]);

  const pop = useMemo(() => {
    const p = data?.village?.populations || [];
    return p.length ? [...p].sort((a, b) => b.year - a.year)[0] : null;
  }, [data]);

  if (error) return <div className="p-10 text-slate-600" data-testid="village-print-error">{error}</div>;
  if (!data) return <div className="p-10 text-slate-500">Yükleniyor...</div>;

  const { village: v, totals, work_logs, infrastructure, petitions, investments } = data;
  const infraTotal = infrastructure.reduce((s, p) => s + (p.actual_cost || 0), 0);
  const openPet = petitions.filter((p) => p.status !== "sonuclandi");
  const activeInfra = infrastructure.filter((p) => p.status !== "kesin_kabul");
  const cut = (arr) => [arr.slice(0, MAX_ROWS), Math.max(0, arr.length - MAX_ROWS)];
  const [wl, wlMore] = cut(work_logs);
  const [inf, infMore] = cut(activeInfra);
  const [pet, petMore] = cut(openPet);
  const [inv, invMore] = cut(investments);

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-8 print:py-0">
      <style>{"@page { size: A4 portrait; margin: 12mm; } @media print { .no-print { display: none !important; } }"}</style>
      <div className="no-print max-w-[210mm] mx-auto mb-3 flex justify-end">
        <button onClick={() => window.print()} data-testid="village-print-now-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors">
          <Printer className="h-4 w-4" /> Yazdır / PDF olarak kaydet
        </button>
      </div>
      <SectionPicker value={show} onChange={updateShow} />
      <article className="bg-white max-w-[210mm] mx-auto p-[12mm] print:p-0 shadow-lg print:shadow-none text-slate-900"
               data-testid="village-print-sheet">
        <header className="flex items-end justify-between gap-4 border-b-2 border-slate-900 pb-2">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && <img src={logoUrl} alt="Birlik logosu" className="h-14 w-auto max-w-[45mm] object-contain" data-testid="print-logo" />}
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-slate-600" data-testid="print-union-name">{union.name}</div>
              <h1 className="text-[20px] font-bold leading-tight">{v.name} Köyü Özet Bilgi Formu</h1>
              {contact && <div className="text-[9.5px] text-slate-500 mt-0.5" data-testid="print-union-contact">{contact}</div>}
            </div>
          </div>
          <div className="text-[10px] text-slate-600 text-right">Düzenleme tarihi<br />{new Date().toLocaleDateString("tr-TR")}</div>
        </header>

        {show.info && (<div data-testid="print-info">
        <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
          <Box label={`Nüfus${pop ? ` (${pop.year})` : ""}`} value={pop ? pop.population.toLocaleString("tr-TR") : "-"} />
          <Box label="Hane" value={v.household_count} />
          <Box label="Muhtar" value={v.muhtar_name || "-"} />
          <Box label="Muhtar Tel." value={v.muhtar_phone || "-"} />
        </div>
        <div className="text-[10px] mt-1.5 truncate"><span className="font-semibold">Azalar: </span>
          {v.azalar?.length ? v.azalar.map((a) => `${a.full_name}${a.phone ? ` (${a.phone})` : ""}`).join(", ") : "-"}
        </div>
        </div>)}

        {show.totals && (
        <div className="grid grid-cols-5 gap-2 mt-2" data-testid="print-totals">
          <Box label="Çalışma Tutarı" value={formatMoney(totals.work_amount)} />
          <Box label="Malzeme Maliyeti" value={formatMoney(totals.material_total)} />
          <Box label="Gerçekleşen Yatırım" value={formatMoney(totals.investment_actual)} />
          <Box label="Altyapı Harcaması" value={formatMoney(infraTotal)} />
          <Box label="Açık Dilekçe" value={totals.petitions_open} />
        </div>
        )}

        {show.work && <Section title="Son Çalışmalar" testId="print-work-logs" empty="Çalışma kaydı yok." more={wlMore}
          headers={["Tarih", "Kategori", "Miktar", "Tutar", "Açıklama"]}
          rows={wl.map((w) => [formatDate(w.date), cats[w.category_id] || "-", `${w.quantity} ${w.unit}`, formatMoney(w.amount), w.description || "-"])} />}
        {show.infra && <Section title="Devam Eden Altyapı Projeleri" testId="print-infra" empty="Devam eden altyapı projesi yok." more={infMore}
          headers={["Proje", "Tip", "Başlangıç", "Durum", "Gerçekleşen"]}
          rows={inf.map((p) => [p.name, INFRA_TYPES[p.type], p.start_date ? formatDate(p.start_date) : "-", STATUS_LABELS[p.status], formatMoney(p.actual_cost)])} />}
        {show.investments && <Section title="Yatırımlar" testId="print-investments" empty="Yatırım kaydı yok." more={invMore}
          headers={["Başlangıç", "Başlık", "Yer / Bölge", "Gerçekleşen", "Durum"]}
          rows={inv.map((i) => [i.start_date ? formatDate(i.start_date) : i.year, i.title, i.location || "-", formatMoney(i.actual), STATUS_LABELS[i.status]])} />}
        {show.petitions && <Section title="Sonuçlanmayı Bekleyen Dilekçeler" testId="print-petitions" empty="Bekleyen dilekçe yok." more={petMore}
          headers={["Tarih", "Dilekçe Sahibi", "Tür", "Konu", "Durum"]}
          rows={pet.map((p) => [formatDate(p.date), p.applicant_name, PETITION_TYPES[p.petition_type], p.subject, STATUS_LABELS[p.status]])} />}

        {show.signatures && (
        <footer className="grid grid-cols-2 gap-10 mt-6 text-[10px] break-inside-avoid" data-testid="print-signatures">
          <div className="text-center"><div className="border-t border-slate-500 pt-1">Düzenleyen<br />{union.name}</div></div>
          <div className="text-center"><div className="border-t border-slate-500 pt-1">Teslim Alan<br />{v.name} Köyü Muhtarı</div></div>
        </footer>
        )}

        <div className="mt-5 pt-1.5 border-t border-slate-200 text-[9px] text-slate-500 text-center" data-testid="print-union-footer">
          {[union.name, contact].filter(Boolean).join(" · ")}
        </div>
      </article>
    </div>
  );
}
